import React, { useEffect, useState } from 'react';

import { getOverview } from '../mocks/mockApi';

import AllocationBreakdownCard from './components/AllocationBreakdownCard';
import MetricCard from './components/MetricCard';
import { ProjectionsAndAllocations } from './components/ProjectionsAndAllocations';

/**
 * Overview & Allocation tab content
 * Displays inventory overview metrics and allocation breakdown
 *
 * @param {Object} props
 * @param {Object|null} props.selectedFacility - The currently selected facility
 * @param {string} props.selectedFacility.value - Facility ID or 'ALL'
 * @param {string} props.selectedFacility.label - Display label (e.g., "BWI - Baltimore")
 * @param {string} props.selectedFacility.name - Facility name (e.g., "BWI")
 */
export function OverviewAllocation({ selectedFacility }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const response = await getOverview(selectedFacility?.value);
            if (response.success) {
                setData(response.data);
            }
            setLoading(false);
        };

        if (selectedFacility?.value) {
            fetchData();
        }
    }, [selectedFacility]);

    if (loading || !data) {
        return <div className="text-center py-8">Loading...</div>;
    }

    const { availableInventory, firstParty, qcAudit, qcAuditPercent, totalInventory, wholesale } = data;

    return (
        <div className="space-y-3">
            {/* Header */}
            <div className="flex justify-between items-start">
                <div className="text-left">
                    <h2 className="text-2xl font-bold text-gray-600">Inventory Overview</h2>
                    <p className="text-sm text-gray-500">
                        Current inventory breakdown across 99 SKUs at {selectedFacility?.name || 'All Facilities'}
                    </p>
                </div>
                <button
                    type="button"
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                    <span>↻</span> Reset Bucket Allocations
                </button>
            </div>

            {/* Metric Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <MetricCard title="Total Inventory" value={totalInventory} subtitle="units" />
                <MetricCard
                    title="QC Audit"
                    value={qcAudit}
                    subtitle={`${qcAuditPercent}% of total`}
                    valueColor="text-[#636363]"
                />
                <MetricCard
                    title="Available Inventory"
                    value={availableInventory}
                    subtitle="after QC removal"
                    valueColor="text-[#004693]"
                />
                <MetricCard
                    title="Wholesale Allocated"
                    value={wholesale.initialAllocation}
                    subtitle={`Demand: ${wholesale.demand.toLocaleString()}`}
                    valueColor="text-[#0074a9]"
                />
                <MetricCard
                    title="First Party Allocated"
                    value={firstParty.initialAllocation}
                    subtitle={`Demand: ${firstParty.demand.toLocaleString()}`}
                    valueColor="text-[#d40824]"
                />
            </div>

            {/* Allocation Breakdown Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AllocationBreakdownCard
                    title="Wholesale Allocation"
                    titleColor="text-[#0074a9]"
                    initialAllocation={wholesale.initialAllocation}
                    confirmedOrders={wholesale.confirmedOrders}
                    packed={wholesale.packedQuantity}
                    adjustments={wholesale.adjustments}
                    receipts={wholesale.receivedQuantity}
                    transfers={wholesale.transfers}
                    showInCartAmount={false}
                    remainingAvailable={wholesale.remainingAvailable}
                />
                <AllocationBreakdownCard
                    title="First Party Allocation"
                    titleColor="text-[#d40824]"
                    initialAllocation={firstParty.initialAllocation}
                    confirmedOrders={firstParty.confirmedOrders}
                    packed={firstParty.packedQuantity}
                    adjustments={firstParty.adjustments}
                    receipts={firstParty.receivedQuantity}
                    transfers={firstParty.transfers}
                    inCart={firstParty.inCart}
                    showInCartAmount
                    remainingAvailable={firstParty.remainingAvailable}
                />
            </div>

            {/* SplitPanelSelector Example - Simple Implementation */}
            <div className="mt-8">
                <div className="h-[800px]">
                    <ProjectionsAndAllocations />
                </div>
            </div>
        </div>
    );
}
